import React from 'react';

import { Grid, Typography } from '@mui/material';
import { FormApi } from 'final-form';
import { useNavigate, useParams } from 'react-router';

import { Field, Form } from '@hrlogics/react-components/form';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';

import { useResetUserPasswordMutation } from 'src/api';
import { Button } from 'src/components/common';
import { PasswordField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { validateWithYupSchema } from 'src/utils/yup';

import { ResetPasswordFormFields, ResetPasswordFormValues } from './types';
import { resetEmailFormValidationSchema } from './utils';

export const ResetPasswordForm: React.FC = () => {
  const { passwordResetRequestId } = useParams<{
    [RouterEntitiesId.passwordResetRequest]: string;
  }>();

  const navigate = useNavigate();

  const { createApolloContext } = useNotification();

  const [resetUserPasswordMutation, { loading: changingPassword }] = useResetUserPasswordMutation({
    context: createApolloContext({
      successMessage: 'Password Changed Successfully',
    }),
  });

  const onSubmit = React.useCallback(
    async (data: ResetPasswordFormValues, form: FormApi<ResetPasswordFormValues>) => {
      try {
        await resetUserPasswordMutation({
          variables: {
            newPassword: String(data.newPassword),
            userPasswordResetRequestId: String(passwordResetRequestId),
          },
        });

        form.restart();
        navigate(appRoutes.auth.login);
      } catch (error) {
        if (error instanceof Error) {
          console.error(error);
        }
      }
    },
    [navigate, passwordResetRequestId, resetUserPasswordMutation],
  );

  const onCancel = React.useCallback(() => {
    navigate(appRoutes.auth.login);
  }, [navigate]);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={INITIAL_VALUES}
      validate={validateWithYupSchema(resetEmailFormValidationSchema)}
    >
      {({ dirty, submitting }) => (
        <Grid container spacing={3}>
          <Grid size={12}>
            <Typography fontSize="24px">Create new password</Typography>
            <Typography fontSize="16px" color="text.secondary">
              We’ve just reset the password. Now you can set up the new one or cancel and back to
              log in
            </Typography>
          </Grid>
          <Grid container spacing={3}>
            <Grid size={12}>
              <Field
                name={ResetPasswordFormFields.newPassword}
                fullWidth
                label="New Password"
                required
                component={PasswordField}
              />
            </Grid>
            <Grid size={12}>
              <Field
                name={ResetPasswordFormFields.confirmNewPassword}
                fullWidth
                label="Confirm New Password"
                required
                component={PasswordField}
              />
            </Grid>
          </Grid>

          <Grid
            container
            spacing={3}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            size={12}
          >
            <Grid>
              <Button
                color="error"
                disabled={submitting || changingPassword}
                size="medium"
                type="reset"
                variant="outlined"
                onClick={onCancel}
              >
                cancel
              </Button>
            </Grid>
            <Grid>
              <Button
                disabled={!dirty || submitting || changingPassword}
                loading={submitting || changingPassword}
                variant="contained"
                type="submit"
              >
                Set new password
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Form>
  );
};

const INITIAL_VALUES: ResetPasswordFormValues = {
  [ResetPasswordFormFields.newPassword]: '',
  [ResetPasswordFormFields.confirmNewPassword]: '',
};
